import { IPublicSession } from 'state/session';
import { State } from 'utils/state';

/**
 * AVS-4354: the local-renderer selection (streamingDevice + renderer) is wiped when — and only
 * when — the user actually LEAVES the broadcast-local sharing option while unshared.
 *
 * This used to live in SharingBroadcastLocal's unmount cleanup, which fired on ANY unmount:
 * a comparison-triggered collapse or a dashboard close silently discarded the dealer's
 * selection, while a genuine option switch made with the panel already collapsed ran no
 * cleanup at all — leaking a stale AVE device under 'remote'/'broadcast', where it enables
 * their CTAs (canStartSharing for 'broadcast' keys on hasStreamingDevice) and can raise a
 * spurious DIALOG_SESSION_OVERRIDE in share(). Keying the wipe to the sharingType transition
 * itself covers every unmount timing and keeps the business rule in the state layer.
 *
 * Mutating the in-flight change (instead of a re-entrant setValues) commits the wipe
 * atomically with the sharingType switch, so syncToServer never flushes an intermediate
 * state — the same pattern as experienceSwitchingSanitation.
 */
export function deviceSelectionSanitation(state: State<IPublicSession>) {
  state.on('beforechange', (keys, arrValues, arrOldValues, sessionChanges) => {
    const sharingTypeIdx = keys.indexOf('sharingType');
    if (sharingTypeIdx === -1) {
      return;
    }

    const oldType = arrOldValues[sharingTypeIdx];
    const newType = sessionChanges.sharingType;
    if (oldType !== 'broadcast-local' || newType === 'broadcast-local') {
      return;
    }

    // A change that carries isShared uses the INCOMING value — a share commit that also
    // switches the type must not lose the device it is about to bind (AVS-4343 F7).
    const nextShared = 'isShared' in sessionChanges ? sessionChanges.isShared : state.get('isShared');
    if (nextShared) {
      return;
    }

    // An explicitly-supplied value in the same change (e.g. a remote apply) is authoritative
    // — sanitize only what the change leaves untouched.
    if (!('streamingDevice' in sessionChanges)) {
      const oldDevice = state.get('streamingDevice');
      if (oldDevice !== '') {
        sessionChanges.streamingDevice = '';
        keys.push('streamingDevice');
        arrValues.push('' as IPublicSession[keyof IPublicSession]);
        arrOldValues.push(oldDevice as IPublicSession[keyof IPublicSession]);
      }
    }

    if (!('renderer' in sessionChanges)) {
      const oldRenderer = state.get('renderer');
      if (oldRenderer !== 'cloud') {
        sessionChanges.renderer = 'cloud';
        keys.push('renderer');
        arrValues.push('cloud' as IPublicSession[keyof IPublicSession]);
        arrOldValues.push(oldRenderer as IPublicSession[keyof IPublicSession]);
      }
    }
  });
}
